import {
  useCreateOrUpdateSculptureEditionInExhibition,
  useSyncItinerantExhibitionGroup,
} from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import { FormLayoutElement } from "@/config/forms/general";
import { SculptureEditionInExhibition } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";
import {
  ItinerantExhibitionPanel,
  ItinerantStopDraft,
  itinerantStopFromExhibition,
} from "./ItinerantExhibitionPanel";
import { ItinerantExhibitionPreview } from "./ItinerantExhibitionPreview";
import { ItinerantDrawerConfig } from "./itinerant-drawer-types";
import { useItinerantDrawerChrome } from "./useItinerantDrawerChrome";
import { useItinerantStopSelection } from "./useItinerantStopSelection";

const defaultFields: FormLayoutElement[] = [
  {
    name: "sculptureEditionDansExpositionsNumeroCatalogue",
    type: "text",
    label: "N° catalogue",
    className: "w-full",
  } as FormLayoutElement,
  {
    name: "sculptureEditionDansExpositionsNotes",
    type: "richtext",
    label: "Notes",
    className: "w-full",
  } as FormLayoutElement,
];

const SculptureEditionInExhibitionDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
  itinerantConfig,
}: {
  fields: FormLayoutElement[] | undefined;
  data: SculptureEditionInExhibition;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  itinerantConfig?: ItinerantDrawerConfig;
}) => {
  const schema = z.object({
    sculptureEditionDansExpositionsNumeroCatalogue: z.string().optional(),
    sculptureEditionDansExpositionsNotes: z.string().optional(),
    exhibition: z
      .object({
        expositionsDescriptionNumeroExpo: z.string().optional(),
        expositionsDescriptionResume: z.string().optional(),
      })
      .optional(),
    sculptureEdition: z
      .object({
        sculptureEditionNumeroEnregistrement: z.string().optional(),
        sculptureEditionNotes: z.string().optional(),
      })
      .optional(),
  });

  const { mutate: createOrUpdate } =
    useCreateOrUpdateSculptureEditionInExhibition(queryId, type);

  const { mutate: syncGroup, isPending: isSyncing } =
    useSyncItinerantExhibitionGroup(
      itinerantConfig?.parentId ?? "",
      itinerantConfig?.parentType ?? "sculptureEdition",
      itinerantConfig?.complexRelationKey,
    );

  const [stops, setStops] = useState<ItinerantStopDraft[]>([]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      exhibition: data.exhibition,
      sculptureEdition: data.sculptureEdition,
      sculptureEditionDansExpositionsNumeroCatalogue:
        data.sculptureEditionDansExpositionsNumeroCatalogue ?? "",
      sculptureEditionDansExpositionsNotes:
        data.sculptureEditionDansExpositionsNotes ?? "",
    },
  });

  const { selectedStopId, selectStop, flushStopsForSubmit } =
    useItinerantStopSelection(itinerantConfig, stops, setStops, form, {
      catalogNumber: "sculptureEditionDansExpositionsNumeroCatalogue",
    });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    if (itinerantConfig) {
      const effectiveStops = flushStopsForSubmit();
      const stopsToSync =
        effectiveStops.length > 0
          ? effectiveStops
          : [
              itinerantStopFromExhibition(itinerantConfig.seedExhibition, undefined, {
                catalogNumber:
                  formData.sculptureEditionDansExpositionsNumeroCatalogue ??
                  "",
              }),
            ];
      syncGroup({
        groupId: itinerantConfig.groupId ?? undefined,
        parentType: itinerantConfig.parentType,
        parentId: itinerantConfig.parentId,
        stops: stopsToSync.map((s, index) => ({
          linkId: s.linkId,
          exhibitionId: s.exhibition.id,
          catalogNumber: s.catalogNumber,
          exemplaireExpose: s.exemplaireExpose,
          note: s.note,
          order: index,
        })),
      });
      return;
    }

    const sculptureEditionId = data.sculptureEdition?.id;
    const exhibitionId = data.exhibition?.id;
    if (!sculptureEditionId || !exhibitionId) return;

    createOrUpdate({
      id: data.id,
      sculptureEditionId,
      exhibitionId,
      sculptureEditionDansExpositionsNumeroCatalogue:
        formData.sculptureEditionDansExpositionsNumeroCatalogue,
      sculptureEditionDansExpositionsNotes:
        formData.sculptureEditionDansExpositionsNotes,
    });
  };

  const isFromExhibition = type === "exhibition";
  const previewConfig = isFromExhibition
    ? {
        attachments: data.sculptureEdition?.attachments ?? [],
        type: "sculptureEdition" as const,
        alt:
          data.sculptureEdition?.sculptureEditionNumeroEnregistrement ??
          data.sculptureEdition?.sculptureEditionNumeroInventaire,
      }
    : {
        attachments: data.exhibition?.attachments ?? [],
        type: "exhibition" as const,
        alt:
          data.exhibition?.expositionsDescriptionNumeroExpo ??
          data.exhibition?.expositionsDescriptionTitre,
      };

  const layoutFields =
    fields && fields.length > 0 ? fields : defaultFields;

  const sheetOpen = open ?? false;
  const { showItinerantSidebar, isItinerantContext, fieldsWithHint } =
    useItinerantDrawerChrome(itinerantConfig, layoutFields);

  const catalogWatch = form.watch(
    "sculptureEditionDansExpositionsNumeroCatalogue",
  );
  const previewStops = useMemo(
    () =>
      stops.map((stop) =>
        stop.id === selectedStopId
          ? { ...stop, catalogNumber: catalogWatch ?? stop.catalogNumber }
          : stop,
      ),
    [stops, selectedStopId, catalogWatch],
  );

  return (
    <LinksDrawerLayout
      form={form}
      fields={fieldsWithHint}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
      submitDisabled={isSyncing}
      formFooter={
        isItinerantContext ? (
          <ItinerantExhibitionPreview stops={previewStops} />
        ) : undefined
      }
      sidebar={
        showItinerantSidebar && itinerantConfig ? (
          <ItinerantExhibitionPanel
            open={sheetOpen}
            seedExhibition={itinerantConfig.seedExhibition}
            initialStops={itinerantConfig.initialStops}
            groupId={itinerantConfig.groupId}
            parentType={itinerantConfig.parentType}
            parentId={itinerantConfig.parentId}
            stops={stops}
            onStopsChange={setStops}
            inlineFields={false}
            selectedStopId={selectedStopId}
            onSelectStop={selectStop}
          />
        ) : undefined
      }
    />
  );
};

export default SculptureEditionInExhibitionDrawer;
